import React, { useMemo, useState } from "react";
import { COLUMN_DEFINITIONS, PAGE_SIZE_OPTIONS } from "./table.config";
import TableLayout from "../../layouts/TableLayout";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "react-toastify";
import { Tabs } from "../../shared/Tabs";
import { Card } from "../../shared/Card";
import { Input } from "../../shared/Input";
import { Select } from "../../shared/Select";
import { TextArea } from "../../shared/TextArea";
import { Button } from "../../shared/Button";
import { Toggle } from "../../shared/Toggle";
import { Badge } from "../../shared/Badge";
import {
  feedbackCategoryOptions,
  feedbackImpactOptions,
  feedbackPriorityOptions,
  feedbackStatusOptions,
  feedbackTypeOptions,
} from "../../constants";
import {
  useCreateFeedbackMutation,
  useDeleteFeedbacksMutation,
  useGetFeedbacksQuery,
} from "../../redux/services/feedbacks/feedbacksApi";

const Feedback = () => {
  const { user } = useAuth();
  const parentCompany = user?.parentCompany;
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [form, setForm] = useState({
    type: "bug",
    subject: "",
    message: "",
    category: "",
    impact: "",
    followUpRequested: true,
    contactPreference: "",
  });

  const {
    data: feedbacksResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetFeedbacksQuery(
    { parent: parentCompany },
    { skip: !parentCompany }
  );
  const [deleteFeedbacks, { isLoading: isDeleting }] =
    useDeleteFeedbacksMutation();
  const [createFeedback, { isLoading: isSubmitting }] =
    useCreateFeedbackMutation();

  const feedbacks = feedbacksResponse?.data || [];

  const appContext = useMemo(
    () => ({
      appName: "AttuneLogic Service",
      appType: "service",
      platform: "web",
      appVersion: window?.location?.host || "web",
      buildNumber: window?.location?.hostname || "web",
      releaseChannel: process.env.NODE_ENV || "production",
    }),
    []
  );

  const deviceContext = useMemo(
    () => ({
      deviceName: navigator?.platform || "web",
      modelName: navigator?.userAgent || "browser",
      osName: navigator?.platform || "web",
      osVersion: navigator?.appVersion || "unknown",
      manufacturer: "browser",
      deviceType: "web",
    }),
    []
  );

  const resolvedEnvironment = process.env.NODE_ENV || "production";

  /**
   * Keep support priority dynamic without asking tenants to classify it.
   * @param {string} type
   * @param {string} impact
   * @returns {string}
   */
  const getTicketPriority = (type, impact) => {
    if (type === "feature") {
      return "Medium";
    }

    if (impact === "risk" || impact === "blocked") {
      return "High";
    }

    if (impact === "moderate") {
      return "Medium";
    }

    return "Low";
  };

  /**
   * Normalize select values so the UI shows labels while data uses values.
   * @param {string} value
   * @param {Array<{label: string, value: string}>} options
   */
  const getSelectLabel = (value, options) => {
    if (!value) return "";
    const match = options.find((option) => option.value === value);
    return match?.label || value;
  };

  /**
   * Update form values using shared Input/TextArea event signatures.
   * @param {Object} target
   */
  const handleFormField = (target) => {
    const id = target?.id;
    if (!id) return;
    const shouldUseChecked =
      typeof target?.checked === "boolean" &&
      (target?.type === "checkbox" ||
        target?.type === "radio" ||
        typeof target?.value === "undefined");
    const value = shouldUseChecked ? target.checked : target?.value ?? "";
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  /**
   * Update select values from shared Select component.
   * @param {{ id: string, value: string }} data
   */
  const handleSelectField = (data) => {
    if (!data?.id) return;
    setForm((prev) => ({ ...prev, [data.id]: data.value }));
  };

  /**
   * Build a feedback payload to send to the API.
   */
  const buildFeedbackPayload = () => {
    const subject = String(form.subject ?? "").trim();
    const message = String(form.message ?? "").trim();

    return {
      type: form.type,
      status: "New",
      subject,
      message,
      category: form.category || undefined,
      environment: resolvedEnvironment,
      priority: getTicketPriority(form.type, form.impact),
      impact: form.impact || undefined,
      followUpRequested: form.followUpRequested,
      contactPreference:
        form.contactPreference || user?.email || undefined,
      appContext,
      deviceContext,
    };
  };

  const filteredFeedbacks = useMemo(() => {
    return feedbacks.filter((feedback) => {
      const typeValue = (feedback.type || "").toLowerCase();
      const statusValue = (
        feedback.status || (feedback.resolved ? "Resolved" : "New")
      ).toLowerCase();
      const priorityValue = (feedback.priority || "").toLowerCase();

      const matchesType =
        typeFilter === "all" || typeValue === typeFilter;
      const matchesStatus =
        statusFilter === "all" ||
        statusValue === statusFilter.toLowerCase();
      const matchesPriority =
        priorityFilter === "all" ||
        priorityValue === priorityFilter.toLowerCase();

      return matchesType && matchesStatus && matchesPriority;
    });
  }, [feedbacks, typeFilter, statusFilter, priorityFilter]);

  const ticketStats = useMemo(() => {
    const total = feedbacks.length;
    const resolved = feedbacks.filter(
      (feedback) => (feedback.status || "").toLowerCase() === "resolved"
    ).length;
    const inProgress = feedbacks.filter(
      (feedback) => (feedback.status || "").toLowerCase() === "in progress"
    ).length;
    const critical = feedbacks.filter(
      (feedback) => (feedback.priority || "").toLowerCase() === "critical"
    ).length;
    const open = total - resolved;
    return { total, open, inProgress, resolved, critical };
  }, [feedbacks]);

  const filterTypeOptions = [
    { label: "All types", value: "all" },
    ...feedbackTypeOptions,
  ];
  const filterStatusOptions = [
    { label: "All statuses", value: "all" },
    ...feedbackStatusOptions,
  ];
  const filterPriorityOptions = [
    { label: "All priorities", value: "all" },
    ...feedbackPriorityOptions,
  ];

  const resetForm = () => {
    setForm({
      type: "bug",
      subject: "",
      message: "",
      category: "",
      impact: "",
      followUpRequested: true,
      contactPreference: "",
    });
  };

  const handleSubmit = async () => {
    const subject = String(form.subject ?? "").trim();
    const message = String(form.message ?? "").trim();

    if (!subject || !message) {
      toast("Please fill in all required fields", {
        type: "error",
        toastId: "feedbackRequired",
      });
      return;
    }

    try {
      await createFeedback({ data: buildFeedbackPayload() }).unwrap();
      toast("Ticket submitted", {
        type: "success",
        toastId: "ticketSubmitted",
      });
      resetForm();
      refetch();
    } catch (error) {
      toast("Failed to submit ticket", {
        type: "error",
        toastId: "ticketSubmitFailed",
      });
    }
  };

  const handleDelete = async (items) => {
    const ids = items.map((item) => item?._id).filter(Boolean);
    if (!ids.length) return { status: "error" };

    const res = await deleteFeedbacks({
      ids,
      parent: parentCompany,
    }).unwrap();

    if (res.status === "success") {
      toast("Feedbacks deleted", {
        type: "success",
        toastId: "feedbacksDeleted",
      });
    }

    return res;
  };

  const ticketsContent = (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              Support Tickets
            </div>
            <div className="text-sm text-gray-500">
              Track submitted issues and requests across dispatch, scheduling,
              jobs, billing, and service operations.
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge color="blue" label={`Open ${ticketStats.open}`} />
            <Badge color="yellow" label={`In Progress ${ticketStats.inProgress}`} />
            <Badge color="green" label={`Resolved ${ticketStats.resolved}`} />
            <Badge color="red" label={`Critical ${ticketStats.critical}`} />
          </div>
        </div>
      </Card>

      <Card>
        <div className="grid gap-4 md:grid-cols-3">
          <Select
            id="typeFilter"
            label="Type"
            value={getSelectLabel(typeFilter, filterTypeOptions)}
            options={filterTypeOptions}
            onChange={(data) => setTypeFilter(data.value)}
          />
          <Select
            id="statusFilter"
            label="Status"
            value={getSelectLabel(statusFilter, filterStatusOptions)}
            options={filterStatusOptions}
            onChange={(data) => setStatusFilter(data.value)}
          />
          <Select
            id="priorityFilter"
            label="Priority"
            value={getSelectLabel(priorityFilter, filterPriorityOptions)}
            options={filterPriorityOptions}
            onChange={(data) => setPriorityFilter(data.value)}
          />
        </div>
      </Card>

      <TableLayout
        id="feedback"
        title="Ticket List"
        loading={isLoading || isFetching || isDeleting}
        columns={COLUMN_DEFINITIONS}
        data={filteredFeedbacks}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        handleDelete={handleDelete}
        enableSorting={true}
        emptyState={{
          title: "No tickets yet",
          description: "Create a ticket to start tracking bugs and feedback.",
        }}
      />
    </div>
  );

  const submitContent = (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              Submit a ticket
            </div>
            <div className="text-sm text-gray-500">
              Keep it simple. Share the problem or request and our team will
              review the ticket.
            </div>
          </div>
          <div className="text-xs text-gray-500">
            {appContext.appName} • {appContext.platform} • {appContext.appVersion}
          </div>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
        <Card>
          <div className="grid gap-4 md:grid-cols-2">
            <Select
              id="type"
              label="Ticket type"
              value={getSelectLabel(form.type, feedbackTypeOptions)}
              options={feedbackTypeOptions}
              onChange={handleSelectField}
              required
            />
            <Select
              id="category"
              label="Area"
              value={getSelectLabel(form.category, feedbackCategoryOptions)}
              options={feedbackCategoryOptions}
              onChange={handleSelectField}
            />
            <Input
              id="subject"
              label="Subject *"
              placeholder="Short summary of the issue or request"
              value={form.subject}
              onChange={handleFormField}
              required
            />
          </div>

          <div className="mt-6 space-y-4">
            <TextArea
              id="message"
              label="Details *"
              value={form.message}
              onChange={(event) => handleFormField(event.target)}
              rows={5}
              placeholder="Tell us what happened, what you expected, or what you need."
              required
            />
            <Select
              id="impact"
              label="Impact"
              value={getSelectLabel(form.impact, feedbackImpactOptions)}
              options={feedbackImpactOptions}
              onChange={handleSelectField}
            />
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Toggle
                id="followUpRequested"
                checked={form.followUpRequested}
                onChange={(event) => handleFormField(event.target)}
              />
              <span className="text-sm text-gray-700">
                Can we follow up with you?
              </span>
            </div>
            <Button
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit ticket"}
            </Button>
          </div>

          {form.followUpRequested && (
            <div className="mt-4">
              <Input
                id="contactPreference"
                label="Best contact"
                placeholder="Email or phone"
                value={form.contactPreference || user?.email || ""}
                onChange={handleFormField}
              />
            </div>
          )}
        </Card>

        <div className="space-y-4">
          <Card>
            <div className="text-sm font-semibold text-gray-900">
              Helpful details
            </div>
            <ul className="mt-3 space-y-2 text-sm text-gray-600">
              <li>Tell us which page or workflow you were using.</li>
              <li>Note what blocked your team or slowed work down.</li>
              <li>Choose the closest area even if you are not fully sure.</li>
            </ul>
          </Card>

          <Card>
            <div className="text-sm font-semibold text-gray-900">
              Included automatically
            </div>
            <div className="mt-2 text-sm text-gray-600">
              Browser and environment details are attached to each ticket for
              faster support triage.
            </div>
            <div className="mt-3 text-xs text-gray-500">
              {deviceContext.deviceName} • {deviceContext.osName} •{" "}
              {resolvedEnvironment}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <Tabs
        id="feedbackTabs"
        useSearchParams
        data={[
          { id: "tickets", title: "Tickets", content: ticketsContent },
          { id: "submit", title: "Submit Ticket", content: submitContent },
        ]}
        tabListClasses="border-b border-gray-200 flex gap-6"
        panelClasses="pt-6"
      />
    </div>
  );
};

export default Feedback;
